import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import Tick from "../../../assets/icons/Tick";
import { apiClient } from "../../../utils/API/axios.interceptor";
import { useRazorpay } from "react-razorpay";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../hooks/useAlert";
import { getEnvVariable } from "../../../env.utils";
import { endpoints } from "../../../../endpoints";
import { useAuth } from "../../../Providers/AuthProvider";
import AuthBanner from "../../auth/pages/Auth.banner";

const PremiumSubscription = () => {
  const [selectedPlan, setSelectedPlan] = useState<"yearly" | "monthly">(
    "yearly"
  );
  const { Razorpay } = useRazorpay();
  const { render: AlertRender, showAlert, hideAlert } = useAlert();
  const [subscription, setSubscription] = useState(null);
  const [user, setUser] = useState<any>(null);
  const { refreshSubscription, isLoggedIn } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const navigate = useNavigate();

  const plansIds = {
    monthly: getEnvVariable("VITE_RAZORPAY_MONTHLY_PLAN_ID"),
    yearly: getEnvVariable("VITE_RAZORPAY_YEARLY_PLAN_ID"),
  };

  const createSubscription = async () => {
    setIsLoading(true);
    try {
      const payload = {
        plan_id: plansIds[selectedPlan],
      };

      const response = await apiClient.post(
        endpoints.payments.createSubscription,
        payload
      );
      setSubscription(response.data.subscription);
      setUser(response.data.user);
      return response.data;
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySubscription = async (payload: any) => {
    setIsLoading(true);
    try {
      await apiClient.post(endpoints.payments.verifySubsription, payload);
      navigate("/payment-success", { replace: true });
    } catch (error) {
      showAlert({
        status: "info",
        title: "Invalid Payment",
        message: "Please Try Aagain.",
      });
    } finally {
      setIsLoading(false);
      refreshSubscription();
    }
  };

  // Function to open Razorpay checkout
  const handlePayment = async () => {
    // Ensure subscription is created
    hideAlert();

    if (!isLoggedIn) {
      onOpen();
      return;
    }

    let sub = subscription as any;
    if (!sub) {
      const { subscription } = await createSubscription();

      sub = subscription;
    }
    if (!sub || !sub.id) {
      console.log(sub);
      showAlert({
        status: "error",
        title: "Failed!",
        message: "Payment failed, Please try again",
      });
      return;
    }

    const key = getEnvVariable("VITE_RAZORPAY_KEY");

    const options: any = {
      key, // Your Razorpay API key (public key)
      subscription_id: sub.id,
      image: "https://bellu.ai/bellu.svg",
      name: "bellu.ai",
      description: "Subscription Payment",
      prefill: {
        name: user && user?.displayName ? user?.displayName : "",
        email: user && user?.email ? user?.email : "",
      },
      theme: {
        color: "#000000",
      },
      recurring: true,
      handler: (data: any) => {
        console.log(data, "---data handler");
        handleVerifySubscription(data);
      },
    };

    const razorpayInstance = new Razorpay(options);
    razorpayInstance.on("payment.failed", function (response) {
      showAlert({
        status: "error",
        title: "Payment Failed",
        message: response.error.description,
      });
    });
    razorpayInstance.open();
  };

  const tick = (
    <Box
      position="absolute"
      top={-5}
      right={-5}
      bg="green.400"
      w={"2.6rem"}
      h="2.6rem"
      borderRadius="full"
      //   boxShadow="0 4px 12px rgba(0, 255, 0, 0.2)"
      display={"flex"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Tick fontSize={"1.2rem"} />
    </Box>
  );

  return (
    <Box
      minH="100vh"
      bg={"#0a1930"}
      position="relative"
      overflow="hidden"
      sx={{
        background: "linear-gradient(145deg, black 0%, black 100%)",
      }}
    >
      <VStack
        spacing={8}
        align="center"
        justify="center"
        minH="100vh"
        position="relative"
        zIndex={1}
        px={8}
        py={[4, 8]}
      >
        <Box width={"full"}>{AlertRender}</Box>
        {/* Key Icon with Glow */}
        <Box>
          <svg
            width="150"
            height="150"
            viewBox="0 0 150 150"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background Circle */}
            <circle
              cx="75"
              cy="75"
              r="60"
              fill="url(#premium-gradient)"
              opacity="0.1"
            />

            {/* Premium Icon */}
            <path
              d="M75 30L95 60L115 45L105 90H45L35 45L55 60L75 30Z"
              fill="url(#premium-gradient)"
              stroke="url(#premium-stroke)"
              strokeWidth="2"
            />

            {/* Decorative Elements */}
            <circle cx="75" cy="75" r="4" fill="#FFD700" />
            <circle cx="45" cy="75" r="2" fill="#FFD700" />
            <circle cx="105" cy="75" r="2" fill="#FFD700" />

            <defs>
              <linearGradient
                id="premium-gradient"
                x1="35"
                y1="30"
                x2="115"
                y2="90"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="100%" stopColor="#FFA500" />
              </linearGradient>
              <linearGradient
                id="premium-stroke"
                x1="35"
                y1="30"
                x2="115"
                y2="90"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="100%" stopColor="#FF8C00" />
              </linearGradient>
            </defs>
          </svg>
        </Box>

        <Heading
          fontSize={{ base: "3xl", md: "4xl" }}
          fontWeight="bold"
          color="white"
          textAlign="center"
          textShadow="0 2px 4px rgba(0,0,0,0.3)"
        >
          Get the complete Bellu.ai experience
        </Heading>

        <Text color="gray.300" fontSize={["md", "lg"]} textAlign="center">
          Upgrade to Premium for unlimited access to all courses, exclusive
          features, and more!
        </Text>

        <HStack
          spacing={6}
          w="full"
          maxW="800px"
          justify="center"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          sx={{ pointerEvents: isLoading ? "none" : "inherit" }}
        >
          {/* Annual Plan */}
          <Box
            onClick={() => setSelectedPlan("yearly")}
            flex="1"
            minW={{ base: "100%", md: "300px" }}
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
            cursor={"pointer"}
            transition="transform 0.2s"
            transform={
              selectedPlan === "yearly" ? "translateY(-4px)" : "translateY(2px)"
            }
            position={"relative"}
            _before={{
              content: '""',
              position: "absolute",
              top: -2,
              right: -2,
              bottom: -2,
              left: -2,
              zIndex: -1,
              borderRadius: "xl",
              background:
                selectedPlan === "yearly"
                  ? "linear-gradient(45deg, #FF6B6B, #4ECDC4)"
                  : "none",
            }}
          >
            {selectedPlan === "yearly" && tick}
            <Box
              px={[5, 6]}
              py={[3, 10]}
              bg={selectedPlan === "yearly" ? "white" : "gray.300"}
              borderRadius="xl"
              boxShadow={"none"}
            >
              <VStack align="stretch" spacing={4}>
                <Text fontWeight={"bold"} color="gray.800">
                  Annual
                </Text>
                <Text color="black" fontSize="2xl" fontWeight="bold">
                  ₹199
                  <Text as="span" fontSize="sm" color="gray.600">
                    /month*
                  </Text>
                </Text>
              </VStack>
            </Box>
          </Box>

          {/* Monthly Plan */}
          <Box
            onClick={() => setSelectedPlan("monthly")}
            flex="1"
            minW={{ base: "100%", md: "300px" }}
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
            cursor={"pointer"}
            transition="transform 0.2s"
            transform={
              selectedPlan === "monthly"
                ? "translateY(-4px)"
                : "translateY(2px)"
            }
            position={"relative"}
            _before={{
              content: '""',
              position: "absolute",
              top: -2,
              right: -2,
              bottom: -2,
              left: -2,
              zIndex: -1,
              borderRadius: "xl",
              background:
                selectedPlan === "monthly"
                  ? "linear-gradient(45deg, #FF6B6B, #4ECDC4)"
                  : "none",
            }}
          >
            {selectedPlan === "monthly" && tick}
            <Box
              px={[5, 6]}
              py={[2.5, 6]}
              bg={selectedPlan === "monthly" ? "white" : "gray.300"}
              borderRadius="xl"
              boxShadow={"none"}
            >
              <VStack align="stretch" spacing={4}>
                <Text color="gray.800" fontWeight={"bold"}>
                  Monthly
                </Text>
                <Text color="black" fontSize="2xl" fontWeight="bold">
                  ₹299
                  <Text as="span" fontSize="sm" color="gray.600">
                    /month*
                  </Text>
                </Text>
              </VStack>
            </Box>
          </Box>
        </HStack>

        <Button
          onClick={() => {
            hideAlert();
            handlePayment();
          }}
          colorScheme="green"
          size="lg"
          w={{ base: "full", md: "auto" }}
          px={12}
          boxShadow="0 4px 12px rgba(0, 255, 0, 0.2)"
          _hover={{
            transform: "translateY(-2px)",
            boxShadow: "0 6px 16px rgba(0, 255, 0, 0.3)",
          }}
          isLoading={isLoading}
        >
          Subscribe now
        </Button>

        <Text color="gray.400" fontSize="sm">
          *Billed monthly, cancel anytime. You can turn off auto-renew from your
          settings.
        </Text>
      </VStack>
      <AuthBanner
        defaultAuthState={"signup"}
        open={isOpen}
        onOpen={onOpen}
        onClose={onClose}
      />
    </Box>
  );
};

export default PremiumSubscription;
